/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { FittingSizeManagementService } from '../../sharedServices/fitting-size-management.service';
import { DialogBoxComponent } from '../../shared/dialog-box/dialog-box.component';
import { Subscription } from 'rxjs';
import { PartialySavedFittings } from '../../models/PartialySavedFittings';
import { IFittingSizeComponent } from '../../models/interfaces/IFittingSizeComponent';
import { ActionTypeEnumDto, SheetMetalFittingSizeDto, SheetMetalSizeDto } from 'api/models';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'app-sheet-metal-fitting-size',
  templateUrl: './sheet-metal.component.html'
})
export class SheetMetalComponent
  implements OnInit, OnDestroy, IFittingSizeComponent {
  columnHeader;
  objectKeys = Object.keys;
  refreshSubscription: Subscription;
  isLoadingSubscription: Subscription;
  partialySavedFittingsSubscription: Subscription;
  addedFromExistingFittingSizeSubscription: Subscription;
  form: FormGroup;
  nominalSizeList: any[];
  hasOnlySavedFittings = true;
  isLoadingResults: boolean;
  selectedElementForDelete: FormGroup;
  contextMenuPosition = { x: '0px', y: '0px' };
  disabledColumns = new Array<string>();
  isAddNewButtonFunctional: boolean;
  isPartialSaveButtonFunctional: boolean;
  isSendForAprovalButtonFunctional: boolean;

  @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;

  @Input() partialSaveActionType: ActionTypeEnumDto;
  @Input() sendForAprovalActionType: ActionTypeEnumDto;
  @Input() deleteActionType: ActionTypeEnumDto;
  @Input() showAddFittingButton = true;
  @Input() showButtons = true;

  constructor(
    public dialog: MatDialog,
    private readonly sharedService: FittingSizeManagementService,
    private readonly formBuilder: FormBuilder) { }

  ngOnInit(): void {
    this.loadSubscriptions();
    this.columnHeader = this.sharedService.getTableHeader();
    this.refresh();
  }

  ngOnDestroy(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
    if (this.partialySavedFittingsSubscription) {
      this.partialySavedFittingsSubscription.unsubscribe();
    }
    if (this.addedFromExistingFittingSizeSubscription) {
      this.addedFromExistingFittingSizeSubscription.unsubscribe();
    }
    this.resetArrayForm([]);
  }

  private loadSubscriptions() {
    this.refreshSubscription = this.sharedService.refreshNeeded.subscribe(
      () => {
        this.refresh();
      }
    );

    this.partialySavedFittingsSubscription = this.sharedService.getPartialySavedFittings.subscribe(
      value => {
        this.setNominalSizesAndAddedFittings(value);
      }
    );

    this.addedFromExistingFittingSizeSubscription = this.sharedService.getAddedFromExistingFittings.subscribe(
      () => {
        this.addExistingFieldValue();
      }
    );
  }

  refresh() {
    this.setDisabledColumns();
    this.resetArrayForm([]);
    this.sharedService.setTableDataList(null);
    this.getNominalSizesAndAddedFittings();
  }

  get newFittingSizes(): FormArray {
    return this.form.get('newFittingSizes') as FormArray;
  }

  hasFittingSelected(): boolean {
    return this.sharedService.hasSelectedFitting();
  }

  isFittingSentForApprove(): boolean {
    return this.sharedService.hasFittingBeenSentToApprove();
  }

  onSizeChange(elementForm: FormGroup) {
    if (this.checkSizeExists(elementForm)) {
      this.sharedService.openSnackBar('Selected Size already exists');
      elementForm.reset();
      return;
    }
    this.setDefaultValues(elementForm);
  }

  getNominalSizesAndAddedFittings() {
    this.sharedService.getNominalSizesAndAddedFittings();
  }

  setNominalSizesAndAddedFittings(value: PartialySavedFittings) {
    this.nominalSizeList = value.NominalSizeList;
    value.PartialySavedFittings
      ? this.resetArrayForm(value.PartialySavedFittings.controls)
      : this.resetArrayForm([]);
  }

  editSelectedField(row: SheetMetalFittingSizeDto) {
    row.isPartialySaved = false;
    const objectKeys = Object.keys(this.selectedElementForDelete.controls);
    this.selectedElementForDelete.controls.IsPartialySaved.setValue(false);
    objectKeys.forEach(key => {
      if (!this.disabledColumns.includes(key)) {
        this.selectedElementForDelete.controls[key].enable();
      }
    });
  }

  addFieldValue() {
    const newItem = {} as SheetMetalSizeDto;
    this.newFittingSizes.push(this.sharedService.getMappedFormGroup(newItem));
    this.resetArrayForm(this.newFittingSizes.controls);
  }

  addExistingFieldValue() {

    if (!this.updateSizeAlreadyExists(this.sharedService.selectedExistingFittingSize.BDID)) {
      this.newFittingSizes.push(
        this.sharedService.getMappedFormGroupWithExistingValues(
          this.nominalSizeList,
          this.sharedService.selectedExistingFittingSize
        )
      );
    }
    this.resetArrayForm(this.newFittingSizes.controls);
  }

  removeFieldValue(comment: any) {
    this.isLoadingResults = true;
    const indexToDelete = this.newFittingSizes.controls.findIndex(
      element => element === this.selectedElementForDelete
    );
    this.newFittingSizes.removeAt(indexToDelete);
    this.resetArrayForm(this.newFittingSizes.controls);

    if (this.selectedElementForDelete.controls.IsPartialySaved.value) {
      this.sharedService.removeSavedRecord(
        this.selectedElementForDelete.controls.ID.value,
        this.deleteActionType, comment
      );
    } else {
      this.isLoadingResults = false;
    }
  }

  onRemoveSavedRecordClick() {
    const dialogRef = this.dialog.open(DialogBoxComponent, {
      data: {
        title: 'Remove record',
        message:
          'Are you sure do you want to remove this parial saved record ?',
        addComment: true,
        comment: ''
      }
    });
    this.handleRemoveSavedRecord(dialogRef);
  }

  handleRemoveSavedRecord(dialogRef: MatDialogRef<DialogBoxComponent, any>) {
    const subscribeOk = dialogRef.componentInstance.okClickEvent.subscribe(
      () => {
        if (dialogRef.componentInstance.data.comment !== '') {
          this.removeFieldValue(dialogRef.componentInstance.data.comment);
        } else {
          this.sharedService.openSnackBar('Add a comment before deleting the partial saved record');
        }
      }
    );

    dialogRef.afterClosed().subscribe(() => {
      subscribeOk.unsubscribe();
    });
  }

  onPartialSaveClick() {
    const dialogRef = this.dialog.open(DialogBoxComponent, {
      data: {
        title: 'Partial Save Information',
        message: 'Are you sure do you want to Partial Save the added Fittings ?'
      }
    });
    this.handlePartialSaveDialogButtons(dialogRef);
  }

  onSendForAprovalClick() {
    const dialogRef = this.dialog.open(DialogBoxComponent, {
      data: {
        title: 'Send for Approval Information',
        message:
          'Are you sure do you want to Send For Approval? All added fittings will be automaticaly saved.',
        addComment: true,
        comment: ''
      }
    });
    this.handleSendForAprovalDialogButtons(dialogRef);
  }


  handlePartialSaveDialogButtons(
    dialogRef: MatDialogRef<DialogBoxComponent, any>
  ) {
    const subscribeOk = dialogRef.componentInstance.okClickEvent.subscribe(
      () => {
        if (this.partialSaveActionType === ActionTypeEnumDto.PartialSaveDelete) {
          this.sharedService.prepareNewAddedFittings(
            this.newFittingSizes,
            this.partialSaveActionType
          );
          return;
        }
        if (this.form.valid) {
          this.sharedService.prepareNewAddedFittings(
            this.newFittingSizes,
            this.partialSaveActionType
          );
        } else {
          this.showInvalidControls();
          this.sharedService.openSnackBar(
            'Please complete all required fields'
          );
          return;
        }
      }
    );
    dialogRef.afterClosed().subscribe(() => {
      subscribeOk.unsubscribe();
    });
  }

  handleSendForAprovalDialogButtons(
    dialogRef: MatDialogRef<DialogBoxComponent, any>
  ) {
    const subscribeOk = dialogRef.componentInstance.okClickEvent.subscribe(
      () => {
        if (
          this.form.valid &&
          dialogRef.componentInstance.data.comment !== ''
        ) {
          this.sharedService.executeSendForAproval(
            this.sendForAprovalActionType,
            dialogRef.componentInstance.data.comment
          );
        } else {
          this.sharedService.openSnackBar('Please add a comment before sending for approval');
        }
      }
    );
    dialogRef.afterClosed().subscribe(() => {
      subscribeOk.unsubscribe();
    });
  }
  showInvalidControls() {
    this.newFittingSizes.controls.forEach(item => {
      item.markAllAsTouched();
    });
  }

  isPartialSaveDisabled(): boolean {
    return this.hasFittingSelected() &&
      this.newFittingSizes.length === 0
      ? true
      : false;
  }

  isSendForApprovalDisabled(): boolean {
    return this.hasElementsNotPartialySaved(this.newFittingSizes) ||
      this.hasFittingSelected() &&
      (this.newFittingSizes.length === 0 ||
        this.sharedService.isFittingDisabled() ||
        this.isFittingSentForApprove())
      ? true
      : false;
  }

  hasElementsNotPartialySaved(fittingsArray: FormArray): boolean {
    const array = fittingsArray.controls as FormGroup[];
    return array.some(code => code.controls.isPartialySaved.value === null || code.controls.isPartialySaved.value === false);
  }

  isAddNewSizeDisabled() {
    return this.hasFittingSelected() && this.isFittingSentForApprove()
      ? true
      : false;
  }
  onContextMenuClick(mouseEvent: MouseEvent, element: any) {
    mouseEvent.preventDefault();
    this.contextMenuPosition.x = mouseEvent.clientX + 'px';
    this.contextMenuPosition.y = mouseEvent.clientY + 'px';
    this.selectedElementForDelete = element;
    this.contextMenu.openMenu();
  }

  setDefaultValues(elementForm: FormGroup) {
    if (
      elementForm.controls.sizeID.value &&
      elementForm.controls.sizeID2.value
    ) {
      const elementObject = this.sharedService.dataType.mapToObject(
        elementForm.controls
      );
      this.sharedService.setDefaultValues(elementForm, elementObject);
    }
  }

  checkSizeExists(elementForm: FormGroup): boolean {
    if (
      elementForm.controls.sizeID.value &&
      elementForm.controls.sizeID2.value
    ) {
      return this.sizeAlreadyExists(
        elementForm.controls.sizeID.value.sizeID,
        elementForm.controls.sizeID2.value.sizeID
      );
    }
    return false;
  }

  updateSizeAlreadyExists(bdid: string): boolean {
    let isExisting = false;
    this.newFittingSizes.controls.forEach(element => {
      if (element.get('bdid').value === bdid) {
        isExisting = true;
      }
    });
    return isExisting;
  }

  sizeAlreadyExists(
    firstSelectedId: number,
    secondSelectedId: number
  ): boolean {
    const fittingsList = this.newFittingSizes.controls as FormGroup[];
    const existingFitttingSizes = new Array<any>();
    fittingsList.forEach(element => {
      if (
        element.controls.sizeID.value &&
        element.controls.sizeID2.value
      ) {
        if (
          element.controls.sizeID.value.sizeID === firstSelectedId &&
          element.controls.sizeID2.value.sizeID === secondSelectedId
        ) {
          existingFitttingSizes.push(element);
        }
      }
    });
    return existingFitttingSizes.length >= 2 ? true : false;
  }

  resetArrayForm(controlsArray) {
    this.form = this.formBuilder.group({
      newFittingSizes: this.formBuilder.array(controlsArray)
    });
  }

  setDisabledColumns() {
    this.disabledColumns = ['NominalSize', 'BDID', 'SM_SizeID', 'DecSize'];
  }
}
